import { useMemo, useState } from "react";

import { Button, EmptyState, Kpi, PageHead, Tabs, type TabItem } from "@/components/common";
import { Guard } from "@/auth/Guard";
import { InviteModal } from "@/components/invitations/InviteModal";
import { useActiveWorkspace } from "@/contexts/WorkspaceContext";
import { useClients } from "@/hooks/useClients";
import { useOrganizations } from "@/hooks/useOrganizations";
import { ClientCreateModal } from "./ClientCreateModal";
import { ClientsList } from "./ClientsList";

type SubFilter = "all" | "active" | "inactive";

export function ClientsPage() {
  const { activeWorkspaceId } = useActiveWorkspace();
  const { data: clients = [] } = useClients(activeWorkspaceId);
  const { data: orgs = [] } = useOrganizations(activeWorkspaceId);
  const [sub, setSub] = useState<SubFilter>("all");
  const [inviting, setInviting] = useState(false);
  const [creating, setCreating] = useState(false);

  const counts = useMemo(() => {
    const ativas = clients.filter((c) => c.active).length;
    return { todas: clients.length, ativas, inativas: clients.length - ativas };
  }, [clients]);

  if (activeWorkspaceId == null) {
    return (
      <>
        <PageHead title="Empresas" subtitle="Clientes vinculados às organizações" />
        <EmptyState icon="briefcase" title="Selecione um workspace" />
      </>
    );
  }

  const tabs: TabItem<SubFilter>[] = [
    { id: "all", label: "Todas", count: counts.todas },
    { id: "active", label: "Ativas", count: counts.ativas },
    { id: "inactive", label: "Inativas", count: counts.inativas },
  ];

  return (
    <>
      <PageHead
        title="Empresas"
        subtitle={`${counts.todas} empresas no workspace`}
        actions={
          <>
            <Guard permission="users.invitations.create">
              <Button variant="ghost" iconLeft="users" onClick={() => setInviting(true)}>
                Convidar membros
              </Button>
            </Guard>
            <Guard permission="clients.write">
              <Button variant="primary" iconLeft="plus" onClick={() => setCreating(true)}>
                Adicionar empresa
              </Button>
            </Guard>
          </>
        }
      />

      <div className="kpi-grid kpi-grid-3">
        <Kpi
          icon="briefcase"
          tone="featured"
          label="Empresas ativas"
          value={String(counts.ativas)}
          sub={`${counts.todas} no total`}
        />
        <Kpi
          icon="alert-triangle"
          label="Inativas"
          value={String(counts.inativas)}
          sub="arquivadas ou pausadas"
        />
        <Kpi
          icon="layers"
          label="Organizações"
          value={String(orgs.length)}
          sub="agrupando empresas"
        />
      </div>

      <Tabs items={tabs} active={sub} onChange={setSub} />

      <ClientsList workspaceId={activeWorkspaceId} status={sub} hideCreate />

      {inviting && (
        <InviteModal kind="user" workspaceId={activeWorkspaceId} onClose={() => setInviting(false)} />
      )}
      {creating && (
        <ClientCreateModal workspaceId={activeWorkspaceId} onClose={() => setCreating(false)} />
      )}
    </>
  );
}
